import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import TocIcon from '@mui/icons-material/Toc'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TableBase from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCellBase from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import Typography from '@mui/material/Typography'
import { MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Tag } from '~/components/Atoms/Tag'
import { DownloadContext } from '~/components/Molecules/DownloadContext'
import { PDFViewer } from '~/components/Organisms/PDFViewer'

import { TableLoading } from './Table.loading'
import { TableCell, TableContainer, TableRow, TableSortLabel } from './Table.styles'

import { Reports } from '~/types/api'

const headCell = [
  {
    id: 'name',
    label: 'reports:cell.name',
  },
  {
    id: 'exercice',
    label: 'reports:cell.exercice',
  },
  {
    id: 'period',
    label: 'reports:cell.period',
  },
  {
    id: 'publication',
    label: 'reports:cell.publication',
  },
]

export function Table({ data, isLoading }: { data: Reports[] | undefined; isLoading: boolean }) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [orderBy, setOrderBy] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [currentId, setCurrentId] = useState<null | string>(null)
  const [currentName, setCurrentName] = useState<null | string>(null)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const onOrder = (id: string) => {
    if (!orderBy || orderBy !== id) {
      setSortOrder('desc')
      setOrderBy(id)
    } else {
      if (sortOrder === 'desc') {
        setSortOrder('asc')
      } else {
        setOrderBy(null)
        setSortOrder('desc')
      }
    }
  }

  const sortedData = useMemo(() => {
    if (!data) return []
    if (!orderBy) return data

    const d = data.slice(0).sort((a, b) => {
      switch (orderBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'exercice':
          return a.year - b.year
        case 'period':
          if (a.period_label === b.period_label) return 0
          if (a.period_label === 'year') return 1
          if (b.period_label === 'year') return -1
          return a.period_label.localeCompare(b.period_label)
        case 'publication':
          return new Date(a.publication_date).getTime() - new Date(b.publication_date).getTime()
      }
    })
    return sortOrder === 'asc' ? d : d.reverse()
  }, [data, orderBy, sortOrder])

  return (
    <TableContainer>
      <TableBase stickyHeader>
        <TableHead>
          <TableRow>
            {headCell.map((cell) => (
              <TableCell align={cell.id === 'period' ? 'center' : 'left'} key={cell.id}>
                <TableSortLabel
                  onClick={() => {
                    onOrder(cell.id)
                  }}
                  active={orderBy === cell.id}
                  direction={orderBy === cell.id ? sortOrder : 'desc'}
                >
                  <Typography variant="body">{t(cell.label)}</Typography>
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell width={'100px'}>
              <Typography variant="body">{t('reports:cell.action')}</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableLoading />
          ) : (
            data &&
            data.length >= 1 &&
            sortedData.map((d) => (
              <TableRow
                onClick={() => {
                  setOpenModal(true)
                  setCurrentId(d.id)
                  setCurrentName(d.name)
                }}
              >
                <TableCell>
                  <Typography variant="body">{d.name}</Typography>
                </TableCell>
                <TableCell width={'150px'}>
                  <Typography variant="body">{d.year}</Typography>
                </TableCell>
                <TableCell width={'150px'} sx={{ padding: `0 ${theme.size[16]}` }} align="center">
                  <Tag
                    width="100%"
                    variant="secondary"
                    label={
                      d.period_label.startsWith('Q') ? d.period_label : t(`reports:cell.periodType.${d.period_label}`)
                    }
                  />
                </TableCell>
                <TableCell width={'200px'}>
                  <Typography variant="body">{d.publication_date}</Typography>
                </TableCell>
                <TableCell width={'100px'}>
                  <Box display="flex">
                    <IconButton
                      onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation()
                        setOpen(true)
                        setAnchorEl(event.currentTarget)
                        setCurrentId(d.id)
                      }}
                    >
                      <DownloadOutlinedIcon />
                    </IconButton>
                    <IconButton
                      onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation()
                        setOpenModal(true)
                        setCurrentId(d.id)
                        setCurrentName(d.name)
                      }}
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
          {data && data.length < 1 && (
            <TableRow sx={{ ':hover': { backgroundColor: 'white' }, height: '100%' }}>
              <TableCellBase colSpan={5} rowSpan={10}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={theme.spacing.L}
                >
                  <TocIcon
                    sx={{ width: theme.size[17], height: theme.size[17], fill: theme.palette.text.placeholder }}
                  />
                  <Typography variant="body">{t('reports:noReports')}</Typography>
                </Box>
              </TableCellBase>
            </TableRow>
          )}
        </TableBody>
      </TableBase>
      <DownloadContext anchorEl={anchorEl} id={currentId} open={open} setOpen={setOpen} />
      <PDFViewer name={currentName} id={currentId} open={openModal} setOpen={setOpenModal} />
    </TableContainer>
  )
}
